import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { NumberParam, StringParam, useQueryParam } from "use-query-params";
import {
  Button,
  StartPage,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { zonesService } from "../../../../api";
import { icons } from "../../../../icons";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import { formatDate } from "../../../../utils/date-formatter";

type Zone = {
  id: string;
  name: string;
  email: string;
  description: string;
  ttl: number;
  type: string;
  status: string;
  serial: number;
  created_at: string;
  updated_at: string;
  project_id: string;
  version: number;
  masters: any[];
  attributes: any;
  service?: any;
};

const ZonesList: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();

  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [zones, setZones] = useState<Zone[]>([]);
  const [paginationData, setPaginationData] = useState<any>(null);

  const [page] = useQueryParam("page", NumberParam);
  const [ordering] = useQueryParam("ordering", StringParam);
  const [pageSize] = useQueryParam("page_size", NumberParam);
  const [searchString] = useQueryParam("search", StringParam);

  const updateList = useCallback(() => {
    setPageLoading(true);
    const params: Record<string, any> = {
      page: page || 1,
      page_size: pageSize || 10,
      search: searchString || "",
    };
    if (ordering) {
      params.ordering = ordering;
    }
    zonesService
      .list(params)
      .then((res) => {
        const data = res.data;
        const objects = (data?.objects || []) as Zone[];
        setZones(objects);
        setPaginationData(data);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_ZONES");
        viewAlert({
          severity: "error",
          message,
        });
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [page, pageSize, ordering, searchString, t, viewAlert]);

  useEffect(() => {
    updateList();
  }, [updateList]);

  const handleDelete = useCallback(
    (zone: Zone) => {
      const confirmDelete = window.confirm(
        t("CONFIRM_DELETE_ZONE", { name: zone.name })
      );
      if (!confirmDelete) {
        return;
      }
      setPageLoading(true);
      zonesService
        .delete(zone.id)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("ZONE_DELETED"),
          });
          updateList();
        })
        .catch((error) => {
          const message =
            error?.response?.data?.detail ||
            extractErrorMessage(error?.response?.data) ||
            t("FAILED_TO_DELETE_ZONE");
          viewAlert({
            severity: "error",
            message,
          });
        })
        .finally(() => {
          setPageLoading(false);
        });
    },
    [t, updateList, viewAlert]
  );

  const columns = useMemo(
    () => [
      {
        title: t("NAME"),
        key: "name",
        sorted: true,
      },
      {
        title: t("STATUS"),
        key: "status",
        sorted: true,
      },
      {
        title: t("CREATED_AT"),
        key: "created_at_display",
        sorted: true,
      },
      {
        title: t("EMAIL"),
        key: "email",
      },
      {
        title: "",
        key: "props",
      },
    ],
    [t]
  );

  const items = useMemo(
    () => [
      {
        title: t("EDIT"),
        id: "edit",
        icon: icons.Edit,
        divider: false,
        onClick: (zone: Zone) => {
          navigate(`/openstack/zones/${zone.id}/edit`);
        },
      },
      {
        title: t("DELETE"),
        id: "delete",
        icon: icons.DeleteIcon,
        divider: false,
        onClick: (zone: Zone) => {
          handleDelete(zone);
        },
      },
    ],
    [t, navigate, handleDelete]
  );

  const tableRows = useMemo(
    () =>
      zones.map((zone) => ({
        ...zone,
        created_at_display: formatDate(zone.created_at),
        status: zone.status?.toUpperCase() || "",
        navigate: `/openstack/zones/${zone.id}`,
      })),
    [zones]
  );

  return (
    <StartPage
      title={t("DNS_ZONES")}
      noResourceText={t("NO_DNS_ZONES")}
      createResourceButton={t("CREATE_ZONE")}
      createResourceLink="/openstack/zones/create"
      haveResource={zones.length > 0}
      pageLoading={pageLoading}
      tableData={{
        columns: columns,
        data: tableRows,
        hideCheckbox: true,
        props: items,
        paginationData: paginationData,
        updateData: updateList,
      }}
    />
  );
};

export default ZonesList;
